import { Close, RemoveCircleOutlined } from '@mui/icons-material';
import Add from '@mui/icons-material/Add';
import { ObjectId } from 'bson';
import PropTypes from 'prop-types';

import React, { Fragment } from 'react';
import {
  Typography,
  Box,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Button,
  FormHelperText,
  FormLabel,
} from '@mui/material';
import TeamMembershipRoleSelect from 'components/@home/drawers/TeamMembershipRoleSelect';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { I18n } from 'utils/i18n';
import { darken } from '@mui/material/styles';

const selectSx = {
  width: '100%',
  maxWidth: '100%',
  '& .MuiSelect-select': {
    padding: '8.5px',
    lineHeight: '1.4375em',
  },
};

const RuleCard = ({ index, remove: removeMe }) => {
  const conditionsName = `rules.${index}.conditions`;
  const { fields, append, remove } = useFieldArray({
    name: conditionsName,
  });
  const { watch, setValue } = useFormContext();
  const roleName = `rules.${index}.role`;
  const isVisibleName = `rules.${index}.isVisible`;
  return (
    <Box
      sx={theme => ({
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '1px',
        border: '1px solid',
        borderColor: theme.palette.divider,
        borderRadius: '8px',
        padding: '16px',
        alignItems: 'baseline',
        paddingRight: '24px',
        marginRight: '20px',
      })}
    >
      {fields.map((field, index2) => {
        const showValue = watch(`rules.${index}.conditions.${index2}.operator`) !== 'has_any_value';
        return (
          <Fragment key={field._id}>
            {index2 > 0 && (
              <Typography
                variant="caption"
                sx={{
                  color: 'muted.main',
                  alignSelf: 'center',
                  marginBottom: 0.5,
                }}
              >
                {I18n.t('RulesDrawer.And')}
              </Typography>
            )}
            <Box
              key={field._id}
              sx={[
                {
                  display: 'grid',
                  gap: '0 0.5rem',
                  gridTemplateColumns: '35% 25% min-content',
                  '& .MuiSelect-select': {
                    padding: '8px',
                  },
                  width: '100%',
                },
                showValue && {
                  gridTemplateColumns: '35% 20% 35% min-content',
                },
              ]}
            >
              <Controller
                render={({
                  field: { onChange, value, name: fieldName },
                  fieldState: { invalid, error },
                }) => (
                  <FormControl fullWidth sx={{ minWidth: 0 }} error={invalid} size="small">
                    <InputLabel id={fieldName} shrink={false}>
                      {!value && I18n.t('RulesDrawer.formFields.field')}
                    </InputLabel>
                    <Select labelId={fieldName} sx={selectSx} value={value} onChange={onChange}>
                      {['code', 'department', 'jobTitle', 'location', 'reportsTo', 'tags'].map(
                        fieldValue => (
                          <MenuItem key={fieldValue} value={fieldValue}>
                            {I18n.t(`RulesDrawer.fields.${fieldValue}`)}
                          </MenuItem>
                        ),
                      )}
                    </Select>
                    {invalid && <FormHelperText>{error?.message}</FormHelperText>}
                  </FormControl>
                )}
                name={`${conditionsName}.${index2}.field`}
                rules={{ required: I18n.t('ValidationErrors.Required', { name: '' }) }}
              />

              <Controller
                render={({
                  field: { onChange, value, name: fieldName },
                  fieldState: { invalid, error },
                }) => (
                  <FormControl fullWidth sx={{ minWidth: 0 }} error={invalid} size="small">
                    <InputLabel id={fieldName} shrink={false}>
                      {!value && I18n.t('RulesDrawer.formFields.operator')}
                    </InputLabel>
                    <Select labelId={fieldName} sx={selectSx} value={value} onChange={onChange}>
                      <MenuItem value="in">{I18n.t('RulesDrawer.operators.in')}</MenuItem>
                      <MenuItem value="not_in">{I18n.t('RulesDrawer.operators.notIn')}</MenuItem>
                      <MenuItem value="has_any_value">
                        {I18n.t('RulesDrawer.operators.hasAnyValue')}
                      </MenuItem>
                      <MenuItem value="contains">
                        {I18n.t('RulesDrawer.operators.contains')}
                      </MenuItem>
                      <MenuItem value="not_contains">
                        {I18n.t('RulesDrawer.operators.notContains')}
                      </MenuItem>
                    </Select>
                    {invalid && <FormHelperText>{error?.message}</FormHelperText>}
                  </FormControl>
                )}
                name={`${conditionsName}.${index2}.operator`}
                rules={{ required: I18n.t('ValidationErrors.Required', { name: '' }) }}
              />

              {showValue && (
                <Controller
                  render={({
                    field: { onChange, value: fieldValue, name: fieldName },
                    fieldState: { invalid, error },
                  }) => {
                    const valueOptions = [];
                    return (
                      <Autocomplete
                        multiple
                        freeSolo
                        ChipProps={{
                          size: 'small',
                          sx: { height: 18, margin: '2.5px !important', borderRadius: 1 },
                          deleteIcon: <Close />,
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        }}
                        value={[...fieldValue]}
                        onBlur={event => {
                          const val = event.target.value;
                          if (val) {
                            event.target.value = '';
                            onChange({
                              target: { name: event.target.name, value: [...fieldValue, val] },
                              type: event.type,
                            });
                          }
                        }}
                        clearOnBlur
                        onChange={(event, value) => {
                          onChange({
                            target: { name: event.target.name, value },
                            type: event.type,
                          });
                        }}
                        fullWidth
                        disableClearable
                        options={valueOptions}
                        sx={selectSx}
                        renderInput={({ ref, ...params }) => (
                          <TextField
                            inputRef={ref}
                            {...params}
                            error={invalid}
                            helperText={error?.message}
                            size="small"
                            slotProps={{
                              ...params.slotProps,
                              htmlInput: {
                                ...params.slotProps?.htmlInput,
                                id: fieldName,
                              },
                            }}
                            placeholder={I18n.t('RulesDrawer.formFields.value')}
                          />
                        )}
                      />
                    );
                  }}
                  name={`${conditionsName}.${index2}.value`}
                  rules={{ required: I18n.t('ValidationErrors.Required', { name: '' }) }}
                />
              )}
              <IconButton
                sx={[
                  {
                    top: 0,
                    height: 40,
                  },
                  index2 === 0
                    ? {
                        visibility: 'hidden',
                      }
                    : {
                        visibility: 'visible',
                      },
                ]}
                onClick={() => remove(index2)}
              >
                <Close />
              </IconButton>
            </Box>
          </Fragment>
        );
      })}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 2,
          width: 'calc(90% + 17px)',
        }}
      >
        <Button
          sx={{ textTransform: 'none' }}
          startIcon={<Add />}
          onClick={() =>
            append({
              _id: new ObjectId().toString(),
              operator: '',
              value: [],
              field: '',
            })
          }
        >
          {I18n.t('RulesDrawer.AddCondition')}
        </Button>
        <FormControl size="small" sx={{ flexDirection: 'row', alignItems: 'center', gap: 2 }}>
          <FormLabel sx={{ whiteSpace: 'nowrap' }}>
            {I18n.t('RulesDrawer.formFields.role')}
          </FormLabel>
          <TeamMembershipRoleSelect
            role={watch(roleName) || 'member'}
            isVisible={!!watch(isVisibleName)}
            visibilityDescriptionKey="RulesDrawer.formFields.visibleToGuestsDescription"
            onChange={({ role, isVisible }) => {
              setValue(roleName, role, { shouldDirty: true, shouldValidate: true });
              setValue(isVisibleName, isVisible, { shouldDirty: true });
            }}
          />
        </FormControl>
      </Box>
      <IconButton
        onClick={removeMe}
        sx={theme => ({
          position: 'absolute',
          top: '50%',
          right: 0,
          transform: 'translate(50%, -50%)',
          backgroundColor: theme.palette.background.default,
          '&:hover': {
            backgroundColor: darken(theme.palette.background.default, 0.05),
          },
        })}
      >
        <RemoveCircleOutlined />
      </IconButton>
    </Box>
  );
};

RuleCard.propTypes = {
  index: PropTypes.number.isRequired,
  remove: PropTypes.func.isRequired,
};

export default RuleCard;
